import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState, useEffect } from "react";
import { TRANSACTION_STATUS_META } from "src/entities/transactions/model/statusMeta";
import { ChipComponent } from "src/shared/components/ChipComponent";
import { Can } from "src/shared/lib/rback/Can";
import {
  TransactionDrawerSummary,
  TransactionStep,
  TransactionStepStatus,
  STEP_TYPE_LABEL,
  CLOSEABLE_STATUSES,
  TransactionStatus,
  TransactionFinalStatus,
} from "../../../entities/transactions/model/types";
import {
  useTransactionDetail,
  useTransactionMutations,
} from "../../../entities/transactions/model/useTransactionDetail";

type Props = {
  open: boolean;
  transactionId: string | null;
  summary: TransactionDrawerSummary | null;
  onClose: () => void;
};

function StepDot({ status }: { status: TransactionStepStatus }) {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        bgcolor: status === "success" ? green[500] : red[500],
      }}
    />
  );
}

function Timeline({ steps }: { steps: TransactionStep[] }) {
  return (
    <Stack spacing={0}>
      {steps.map((s, idx) => {
        const isLast = idx === steps.length - 1;

        return (
          <Box key={s.id} sx={{ display: "flex", gap: 2, py: 1 }}>
            <Box
              sx={{
                width: 20,
                position: "relative",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <StepDot status={s.status} />
              </Box>

              {!isLast && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 22,
                    bottom: -10,
                    width: 2,
                    bgcolor: "divider",
                  }}
                />
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={500}>
                {STEP_TYPE_LABEL[s.type]}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Аккаунт: {s.accountId}
              </Typography>
              <ChipComponent
                label={s.status === "success" ? "Успешно" : "Ошибка"}
                backgroundColor={s.status === "success" ? green[100] : red[100]}
                color={s.status === "success" ? green[400] : red[400]}
                sx={{ mt: 1 }}
              />

              {s.error && (
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{ mt: 1, display: "block" }}
                >
                  {s.error}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

export function TransactionDrawer({
  open,
  transactionId,
  summary,
  onClose,
}: Props) {
  const { data, isFetching, isError, refetch } = useTransactionDetail(
    transactionId,
    {
      enabled: open,
    },
  );
  const { closeTransaction, setFinalStatus } = useTransactionMutations();

  const [isActionLoading, setIsActionLoading] = useState(false);

  const canBeClosed = useMemo(
    () => (data?.status ? CLOSEABLE_STATUSES.includes(data.status) : false),
    [data?.status],
  );

  useEffect(() => {
    if (open && transactionId) {
      refetch();
    }
  }, [open, transactionId, refetch]);

  const canChooseFinal =
    data?.status === TransactionStatus.CLOSED_BY_FINANCE_SUPPORT;

  const accountId = data?.steps.find((step) => step.accountId)?.accountId;
  const handleCloseTx = async () => {
    if (!transactionId) return;

    setIsActionLoading(true);
    try {
      await closeTransaction(transactionId);
      await refetch();
    } catch (error) {
      enqueueSnackbar("Не удалось закрыть транзакцию.", {
        variant: "error",
      });
      console.error("Не удалось закрыть транзакцию", error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleFinal = async (finalStatus: TransactionFinalStatus) => {
    if (!transactionId) return;

    setIsActionLoading(true);
    try {
      await setFinalStatus(transactionId, finalStatus);
      await refetch();
    } catch (error) {
      enqueueSnackbar("Не удалось обновить статус транзакции.", {
        variant: "error",
      });
      console.error("Не удалось обновить статус транзакции", error);
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 520 }, p: 2 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography variant="h6">Детализация транзакции</Typography>
          <Typography variant="caption" color="text.secondary">
            ID транзакции: {data?.externalId || ""}
          </Typography>
        </Box>

        <IconButton onClick={onClose} aria-label="Закрыть">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {isFetching ? (
        <Box
          sx={{
            minHeight: 160,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : isError ? (
        <Typography variant="body2" color="error">
          Не удалось получить данные по операциям в транзакции.
        </Typography>
      ) : !data ? (
        <Typography variant="body2" color="text.secondary">
          Нет данных для отображения.
        </Typography>
      ) : (
        <>
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <b>Статус:</b>
              <ChipComponent
                sx={{ ml: 1 }}
                label={TRANSACTION_STATUS_META[data.status].label}
                backgroundColor={
                  TRANSACTION_STATUS_META[data.status].backgroundColor
                }
                color={TRANSACTION_STATUS_META[data.status].color}
              />
            </Typography>
            <Typography variant="body2">
              <b>Клиент:</b> {summary?.clientName ?? "—"}
            </Typography>
            <Typography variant="body2">
              <b>Аккаунт:</b> {accountId ?? "—"}
            </Typography>
            <Typography variant="body2">
              <b>Договор:</b> {summary?.contractNumber ?? "—"}
            </Typography>
          </Stack>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Этапы
          </Typography>

          <Timeline steps={data.steps} />

          {canBeClosed && (
            <Can perform="transactions.manage">
              <>
                <Divider sx={{ my: 2 }} />
                <Button
                  fullWidth
                  variant="contained"
                  disabled={isActionLoading}
                  onClick={handleCloseTx}
                >
                  Закрыть транзакцию
                </Button>
              </>
            </Can>
          )}

          {canChooseFinal && (
            <Can perform="transactions.manage">
              <>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    disabled={isActionLoading}
                    onClick={() => handleFinal("SUCCESSFUL")}
                  >
                    Успешно
                  </Button>

                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    disabled={isActionLoading}
                    onClick={() => handleFinal("FAILED")}
                  >
                    Неуспешно
                  </Button>
                </Stack>
              </>
            </Can>
          )}
        </>
      )}
    </Drawer>
  );
}
